import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";

import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { AutoCompleteTextBox, ButtonUI, KMKDialog } from "../../../../../Common";
import { showToast } from "../../../../../../store/actions";
import TemplateService from "../../../../../../service/template";
import { setPrevState } from "../../../../../../util/common";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root": {
      maxWidth: "710px",
      width: "510px",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiDialogActions-root": {
      position: "absolute",
      bottom: "10px",
      right: "20px",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "150px",
    height: "160px",
  },
  container: {
    padding: "0px 24px 0 24px",
    backgroundColor: "#FFFFFF",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
}));

export default function Template({ Settings }) {
  const [templateState, setTemplateState] = useState({
    template: "",
    templateData: [],
    templateOptions: [],
    loading: false,
    templateError: "",
  });

  const { classes } = useStyles();
  const dispatch = useDispatch();

  const setTemplate = (value) => {
    setPrevState(setTemplateState, { template: value });
  };

  const getTemplateOptions = useCallback(async () => {
    const templateService = new TemplateService();
    const data = await templateService.getData(Settings.type);
    setPrevState(setTemplateState, { loading: true, templateData: data });

    const result = templateService.getAttributeTemplateOptions();

    if (result.length > 0) {
      let options = [];
      result.forEach((item, key) => {
        if (item.id > 0) {
          options[key] = [];
          options[key]["label"] = item.name;
          options[key]["value"] = item.Template_id;
        }
      });

      setPrevState(setTemplateState, { templateOptions: options });
    } else {
      setPrevState(setTemplateState, { templateOptions: [] });
    }

    setPrevState(setTemplateState, { loading: false });
  }, [Settings.type]);

  const handleSave = async () => {
    setPrevState(setTemplateState, { loading: true });
    if (templateState.template.length > 50 || templateState.template.length < 2)
      setPrevState(setTemplateState, { templateError: "Template Name must be between 2 to 50 characters." });
    else {
      let result = {};
      let response = null;

      if (templateState.templateData.length > 0) {
        result = templateState.templateData.filter(
          (item) => item.name.toLowerCase() === templateState.template.toLowerCase()
        );
      }

      const templateJson = {
        name: templateState.template,
        description: "",
        readonly: true,
        version: "2022-02-17",
      };

      const templateService = new TemplateService();
      if (Object.keys(result).length > 0) {
        // Save existing template logic
        templateJson.name = result[0].name;
        const templateId = result[0].Template_id;
        templateJson.tags = Settings?.tagsRef?.current.getTags() || [];

        templateJson.fields = Settings.dataTableRef.current.getData().map(({ key, ...other }) => other);

        response = await templateService.updateAttributeTemplate(templateId, templateJson);
      } else {
        // Save new template logic
        templateJson.tags = Settings?.tagsRef?.current.getTags() || [];
        templateJson.fields = Settings.dataTableRef.current.getData().map(({ key, ...other }) => other);
        let submitData = {
          name: templateState.template,
          type: Settings.type,
          detail: templateJson,
        };

        response = await templateService.createAttributeTemplate(submitData);
      }

      if (response.success) {
        getTemplateOptions();
        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );

        Settings.setOpen(false);
      }
    }

    setPrevState(setTemplateState, { loading: false });
  };

  const handleCancel = () => {
    Settings.setOpen(false);
  };

  const clearFormData = () => {
    setPrevState(setTemplateState, { template: "" });
  };

  useEffect(() => {
    getTemplateOptions();
    return () => {
      clearFormData();
    };
  }, [getTemplateOptions]);

  return (
    <KMKDialog className={classes.dialogPaper} open={Settings.open}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar} variant="dense">
          {Settings.popupTitle}
        </Toolbar>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 4,
            color: "black",
            mr: "10px",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Backdrop
          sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={templateState.loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
          <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
            <Grid size={12}>
              <AutoCompleteTextBox
                configData={{
                  placeHolder: "Template",
                  label: "Template",
                  template: templateState.template,
                  setTemplate: setTemplate,
                  templateOptions: templateState.templateOptions,
                  freeSolo: true,
                  templateError: templateState.templateError,
                  maxLengthValidation: true,
                  textValiation: true,
                }}
              />
            </Grid>
          </Grid>
          <DialogActions>
            <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
              Cancel
            </ButtonUI>
            <ButtonUI
              onClick={handleSave}
              variant="contained"
              color="success"
              style={{ marginLeft: 15 }}
              disabled={!templateState.template || !templateState.template.trim() || templateState.loading}
            >
              Save
            </ButtonUI>
          </DialogActions>
        </Box>
      </Box>
    </KMKDialog>
  );
}
